<template>
  <div class="mask" @click.self="onClickCancel">
    <div
      ref="dialogRoot"
      class="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <div class="dialog__body">
        <p class="dialog__lead">
          {{ $t(leadKey) }}
        </p>
      </div>

      <footer class="dialog__footer">
        <button type="button" class="dialog__ok-button" :disabled="isSubmitting" @click="onClickConfirm">
          {{
            isSubmitting
              ? $t("proofreadingAiConfirmDialog.submitting")
              : $t("proofreadingAiConfirmDialog.confirm", { fish: formattedFish })
          }}
        </button>
        <button type="button" class="dialog__cancel-button" :disabled="isSubmitting" @click="onClickCancel">
          {{ $t("proofreadingAiConfirmDialog.cancel") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { isAiProofreadingSyncUi } from "@/lib/api/proofreadingAi";

export type ProofreadingAiConfirmDialogResult = boolean;

export type ProofreadingAiConfirmDialogOptions = {
  estimatedFish: number;
  isSubmitting?: boolean;
};

export default Vue.extend({
  props: {
    estimatedFish: {
      type: Number,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      titleId: "proofreading-ai-confirm-dialog-title",
    };
  },
  computed: {
    formattedFish(): string {
      return this.estimatedFish.toLocaleString(this.$i18n.locale || "ja");
    },
    leadKey(): string {
      return isAiProofreadingSyncUi()
        ? "proofreadingAiConfirmDialog.leadSync"
        : "proofreadingAiConfirmDialog.lead";
    },
  },
  mounted() {
    this.$nextTick(() => {
      const root = this.$refs.dialogRoot as HTMLElement | undefined;
      root?.focus();
    });
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e: KeyboardEvent) {
      if (e.key !== "Escape" || this.isSubmitting) return;
      this.onClickCancel();
    },
    onClickConfirm() {
      if (this.isSubmitting) return;
      this.$close(true as ProofreadingAiConfirmDialogResult);
    },
    onClickCancel() {
      if (this.isSubmitting) return;
      this.$close(false as ProofreadingAiConfirmDialogResult);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9300;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: visible;

  &:focus {
    outline: none;
  }
}

.dialog__body {
  padding: 24px 24px 8px;
}

.dialog__lead {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
  text-align: center;
  white-space: pre-line;
}

.dialog__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px 24px;
}

.dialog__cancel-button,
.dialog__ok-button {
  position: relative;
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.dialog__cancel-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}

.dialog__ok-button {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}
</style>
